import { reducerWithInitialState } from 'typescript-fsa-reducers'
import { produce } from 'immer'

import { ApiError } from '../accountRoot.reducer'
import { ResultPageWebModel, UserLogin, Subsidiary } from '@professocialApi/professocial-core-frontend-api'
import {
  createUserAccount,
  deleteUserAccount, getAboSettingsForPageIdentifiers,
  getAllSubsidiaries,
  getAllUsers, getPageIdentifiersForUser, getTokenForPage, initiateFacebookConnect,
  listSubsidiaries, loginAs, setOnSuccess,
  unmountFacebookPageByUser,
  updateUserAccount,
} from './accounts.actions'
import { UserAboSettings } from '@3m5/sm_publish-frontend-api'
import { PageIdentifier } from '@professocialApi/professocial-core-frontend-api/models'

export enum SaveSuccess {
  createUserAccount = 'createUserAccount',
  updateUserAccount = 'updateUserAccount',
  deleteUserAccount = 'deleteUserAccount',
  unmountFacebookPageByUser = 'unmountFacebookPageByUser',
}

export interface AccountsState {
  fetching: boolean
  token?: string
  allUserAccounts?: ResultPageWebModel
  allSubsidiaries?: Subsidiary[]
  aboSettings?: UserAboSettings[]
  pageIdentifiers?: PageIdentifier[]
  userAccount?: UserLogin
  facebookConnectHash?: string
  error: ApiError
  saveSuccess?: SaveSuccess | null
  user?: any
  isLogged: boolean
}

const INITIAL_STATE: AccountsState = {
  fetching: false,
  error: {},
  isLogged: false,
}

export const AccountsReducer = reducerWithInitialState(INITIAL_STATE)
  .case(setOnSuccess, (state, saveSuccess) => {
    return ({ ...state, saveSuccess })
  })
/**
   * createAccounts
   */
  .case(createUserAccount.started, state => {
    return produce(state, draft => {
      delete draft.error.createUserAccount
    })
  })
  .case(createUserAccount.failed, (state, { error }) => {
    return produce(state, draft => {
      const errorStatus = (error as Response)
      if (errorStatus.status === 401) {
        draft.isLogged = false
        draft.user = undefined
      }
      draft.error.createUserAccount = (error as Response)
    })
  })

/**
   * deleteUserAccount
   */
  .case(deleteUserAccount.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      draft.saveSuccess = null
      delete draft.error.deleteUserAccount
    })
  })

  .case(deleteUserAccount.done, (state) => {
    return produce(state, draft => {
      draft.saveSuccess = SaveSuccess.deleteUserAccount
      draft.fetching = false
    })
  })

  .case(deleteUserAccount.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      const errorStatus = (error as Response)
      if (errorStatus.status === 401) {
        draft.isLogged = false
        draft.user = undefined
      }
      draft.error.deleteUserAccount = (error as Response)
    })
  })

/**
   * updateUserAccount
   */
  .case(updateUserAccount.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.updateUserAccount
    })
  })

  .case(updateUserAccount.done, (state) => {
    return produce(state, draft => {
      draft.saveSuccess = SaveSuccess.updateUserAccount
      draft.fetching = false
    })
  })

  .case(updateUserAccount.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      const errorStatus = (error as Response)
      if (errorStatus.status === 401) {
        draft.isLogged = false
        draft.user = undefined
      }
      draft.error.updateUserAccount = (error as Response)
    })
  })

/**
   * getAllUsers
   */
  .case(getAllUsers.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getAllUsers
    })
  })

  .case(getAllUsers.done, (state, { result }) => {
    return produce(state, draft => {
      draft.allUserAccounts = result
      draft.fetching = false
    })
  })

  .case(getAllUsers.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      const errorStatus = (error as Response)
      if (errorStatus.status === 401) {
        draft.isLogged = false
        draft.user = undefined
      }
      draft.error.getAllUsers = (error as Response)
    })
  })

/**
   * getAllSubsidiaries
   */
  .case(getAllSubsidiaries.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      draft.saveSuccess = null
      delete draft.error.getAllSubsidiaries
    })
  })

  .case(getAllSubsidiaries.done, (state, { result }) => {
    return produce(state, draft => {
      draft.allSubsidiaries = result
      draft.fetching = false
    })
  })

  .case(getAllSubsidiaries.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      const errorStatus = (error as Response)
      if (errorStatus.status === 401) {
        draft.isLogged = false
        draft.user = undefined
      }
      draft.error.getAllSubsidiaries = (error as Response)
    })
  })

/**
   * listSubsidiaries
   */
  .case(listSubsidiaries.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      draft.saveSuccess = null
      delete draft.error.listSubsidiaries
    })
  })

  .case(listSubsidiaries.done, (state, { result }) => {
    return produce(state, draft => {
      draft.allSubsidiaries = result
      draft.fetching = false
    })
  })

  .case(listSubsidiaries.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      const errorStatus = (error as Response)
      if (errorStatus.status === 401) {
        draft.isLogged = false
        draft.user = undefined
      }
      draft.error.listSubsidiaries = (error as Response)
    })
  })

/**
   * unmountFacebookPageByUser
   */
  .case(unmountFacebookPageByUser.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      draft.saveSuccess = null
      delete draft.error.unmountFacebookPageByUser
    })
  })

  .case(unmountFacebookPageByUser.done, (state) => {
    return produce(state, draft => {
      draft.saveSuccess = SaveSuccess.unmountFacebookPageByUser
      draft.fetching = false
    })
  })

  .case(unmountFacebookPageByUser.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      const errorStatus = (error as Response)
      if (errorStatus.status === 401) {
        draft.isLogged = false
        draft.user = undefined
      }
      draft.error.unmountFacebookPageByUser = (error as Response)
    })
  })

/**
   * loginAs
   */
  .case(loginAs.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.loginAs
    })
  })

  .case(loginAs.done, (state) => {
    return produce(state, draft => {
      draft.fetching = false

      window.location.replace('/user/settings/connect?login_as=true')
    })
  })

  .case(loginAs.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.loginAs = (error as Response)
    })
  })

/**
   * getAboSettingsForPageIdentifiers
   */
  .case(getAboSettingsForPageIdentifiers.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getAboSettingsForCurrentUser
    })
  })

  .case(getAboSettingsForPageIdentifiers.done, (state, { result }) => {
    return produce(state, draft => {
      draft.aboSettings = result
      draft.fetching = false
    })
  })

  .case(getAboSettingsForPageIdentifiers.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getAboSettingsForPageIdentifiers = (error as Response)
    })
  })

/**
   * initiateFacebookConnect
   */
  .case(initiateFacebookConnect.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.performDisconnect
    })
  })

  .case(initiateFacebookConnect.done, (state, { result }) => {
    return produce(state, draft => {
      draft.facebookConnectHash = result.connectHash
      draft.fetching = false
      return draft
    })
  })

  .case(initiateFacebookConnect.failed, (state) => {
    return produce(state, draft => {
      draft.fetching = false
    })
  })

/**
   * getPageIdentifiersForUser
   */
  .case(getPageIdentifiersForUser.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getAboSettingsForCurrentUser
    })
  })

  .case(getPageIdentifiersForUser.done, (state, { result }) => {
    return produce(state, draft => {
      draft.pageIdentifiers = result
      draft.fetching = false
    })
  })

  .case(getPageIdentifiersForUser.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getAboSettingsForPageIdentifiers = (error as Response)
    })
  })

  /**
   * getFacebookTokenForUserPage
   */
  .case(getTokenForPage.started, state => {
    return produce(state, draft => {
      draft.fetching = true
      delete draft.error.getAboSettingsForCurrentUser
    })
  })

  .case(getTokenForPage.done, (state, { result }) => {
    return produce(state, draft => {
      draft.token = result
      draft.fetching = false
    })
  })

  .case(getTokenForPage.failed, (state, { error }) => {
    return produce(state, draft => {
      draft.fetching = false
      draft.error.getFacebookTokenForUserPage = (error as Response)
    })
  })
